import { LicenseTester, Model } from "@mcleod/core";
import {
    ClickEvent, ContextMenuLabelProps, DropdownItem, Image, Label, Overlay, Panel, Snackbar, TableRow,
    TableRowDisplayEvent
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutTriumphPayTxAudit } from "./autogen/AutogenLayoutTriumphPayTxAudit";

export class TriumphPayTxAudit extends AutogenLayoutTriumphPayTxAudit {

    protected tableInvExceptionsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.setupSeverityPanel(tableRow);
        tableRow.contextMenuItems = () => this.generateContextMenuItems(tableRow);
    }

    private generateContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        let selectedRows = tableRow.table.selectedRows;
        // Match brokerage planning - select the clicked row if it wasn't already, and clear
        // other selected rows if there were any
        if (!selectedRows.includes(tableRow)) {
            tableRow.table.selectedRow = tableRow;
            selectedRows = [tableRow]
        }
        return Object.values(TriumphPayTxAudit.severityValues).map(severity => ({
            text: `Mark as ${severity.description}`,
            onClick: () => selectedRows.forEach(row => this.updateSeverity(row, severity.id))
        }));
    }

    private setupSeverityPanel(tableRow: TableRow) {
        const panel = tableRow?.findComponentById("panelSeverity") as Panel;
        const labelSeverity = tableRow?.findComponentById("labelSeverity") as Label;
        const imageSeverityChevron = tableRow.findComponentById("imageSeverityChevron") as Image;
        const severityId = Number(tableRow?.data?.get("severity"));
        const severity = severityId ? TriumphPayTxAudit.severityValues[severityId] : null;
        if (imageSeverityChevron?.busy) {
            imageSeverityChevron.busy = false;
        }
        if (!severity) {
            return;
        }
        if (labelSeverity) {
            labelSeverity.color = severity.color;
            labelSeverity.text = severity.description;
        }
        if (panel) {
            panel.borderColor = severity.color;
        }
        if (imageSeverityChevron) {
            imageSeverityChevron.color = severity.color;
        }
    }

    protected panelSeverityOnClick(event: ClickEvent) {
        const panel = event.target as Panel;
        if (!panel["severityDropDownItems"]) {
            panel["severityDropDownItems"] = Object.values(TriumphPayTxAudit.severityValues).map(severity => ({
                text: severity.description,
                onClick: () => this.updateSeverity(panel["row"], severity.id),
            }));
        }
        Overlay.showDropdown(panel, panel["severityDropDownItems"]);
    }

    private updateSeverity(row: TableRow, id: number): void {
        row.data.set("severity", id);
        row.data.post().then(() => this.setupSeverityPanel(row));
    }

    protected buttonUpdateSeverityOnClick(event: ClickEvent) {
        const selectedRows = this.tableInvExceptions.selectedRows;
        if (selectedRows.length === 0) {
            Snackbar.showSnackbar("Please select at least one row to update.");
            return;
        }
        const dropdownItems = Object.values(TriumphPayTxAudit.severityValues).map(severity =>
            (new DropdownItem(severity.id.toString(), severity.description)));
        CommonDialogs.showInputDialog("Severity",
            `Update Exceptions (${selectedRows.length})`, {
            items: dropdownItems,
            defaultDataValue: dropdownItems[3].value, // Default to "Resolved"
        }).then((value) => {
            if (value) {
                selectedRows.forEach(row => this.updateSeverity(row, Number(value)));
            }
        });
    }

    static async isHubtranActive(): Promise<boolean> {
        const isHubTranLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.hubtran");
        if (!isHubTranLicensed) {
            return false;
        }
        const triumphpayAuditControlRow = await Model.searchSingleRecord("lme/dispatch/hubtran-control");
        return triumphpayAuditControlRow?.get("is_active", "N") === "Y";
    }

    // May need to add textColor property for caution to exactly match mockup
    static severityValues: { [id: number]: { id: number; description: string; color: string } } = {
        1: {
            id: 1,
            description: "Critical",
            color: "error"
        },
        2: {
            id: 2,
            description: "Major",
            color: "warning"
        },
        3: {
            id: 3,
            description: "Minor",
            color: "caution"
        },
        4: {
            id: 4,
            description: "Resolved",
            color: "success"
        }
    }
}
